import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Sprint } from '../models/sprint';
import { MessageService } from './message.service';
import { ConfigLoaderService } from '../config/configloader.service';
import { formatISO } from 'date-fns';
import { ProgramIncrement } from '../models/programIncrement';
import { SynchronizationService } from './synchronization.service';
import { EventSprintUpdated } from '../models/syncEvents/persistenceEvents/eventSprintUpdated';
import { map } from 'rxjs/operators';

/**
 * this class represents access to all rest requests concerning Sprint data of the PyWall
 * this rest service honors isMock from settingservice
 */
@Injectable()
export class SprintService {

  private projectBaseURL: string;

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private syncService: SynchronizationService,
    private configService: ConfigLoaderService) {

    this.projectBaseURL = `${this.configService.settings.pyWallServer.url}projects`;
  }

  /**
   * returns an Array of all sprints from a specific project
   */
  getSprints(projectKey: string): Observable<Sprint[]> {
    this.messageService.sendDebug(`SprintService getSprints called on project ${projectKey}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/sprints/`;
    console.log(targetURL);
    return this.http.get<Sprint[]>(targetURL).pipe(map(res => res));
  }

  /**
   *
   * @param startDate only include sprints that start on this date or later
   * @param endDate  only inlcude sprints that end on this date or before
   * @param duration only include sprints that have a duration of <duration> days.
   */
  getSprintsByDate(projectKey: string, startDate: Date, endDate: Date, duration: number): Observable<Sprint[]> {
    const start = formatISO(new Date(startDate), { representation: 'date' });
    const end = formatISO(new Date(endDate), { representation: 'date' });
    const targetURL = this.projectBaseURL + `/${projectKey}/sprints?start=${start}&end=${end}&duration=${duration}/`;
    return this.http.get<Sprint[]>(targetURL);
  }

  getSprintsForProgramIncrement(projectKey: string, programIncrement: ProgramIncrement): Observable<Sprint[]> {
    return this.getSprintsByDate(projectKey, programIncrement.dateStart, programIncrement.dateEnd, programIncrement.sprintDuration);
  }

  /**
   * returns the details to one specific sprint
   */
  getSprintDetails(projectKey: string, sprintId: number): Observable<Sprint> {
    this.messageService.sendDebug(`SprintService getSprintDetail called on project ${projectKey} on Sprint ${sprintId}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/sprints/${sprintId}/`;
    return this.http.get<Sprint>(targetURL);
  }

  updateSprint(projectKey: string, sprint: Sprint, onSuccessCallback?): void {
    const startDateIso = (sprint.startDate) ? formatISO(new Date(sprint.startDate), { representation: 'date' }) : null;
    const endDateIso = (sprint.endDate) ? formatISO(new Date(sprint.endDate), { representation: 'date' }) : null;

    const data = {
      'name': sprint.name,
      'startDate': startDateIso,
      'endDate': endDateIso,
      'state': sprint.state
    };
    const targetURL = this.projectBaseURL + `/${projectKey}/sprints/${sprint.id}/`;
    this.messageService.sendDebug(`SprintService updateSprint called on sprint ${sprint.name}.`);

    this.http.put<Sprint>(targetURL, data).subscribe({
      next: () => {
        this.syncService.sendPersistenceEventNew(new EventSprintUpdated(sprint));
        if (onSuccessCallback) {
          onSuccessCallback();
        }
      },
      error: (error) => {
        this.messageService.sendWarn(`Couldn't update sprint ${sprint.name} of project ${projectKey}, ${error}`);
      }
    });
  }

  /**
   * Deletes selected Sprint
   */
  deleteSprint(projectKey: string, sprintId: number): Observable<any> {
    const targetURL = this.projectBaseURL + `/${projectKey}/sprints/${sprintId}/`;
    this.messageService.sendDebug('deleted sprint ' + sprintId);
    return this.http.delete<any>(targetURL);
  }

  /**
   * creates a new sprint in the specified project
   */
  createSprint(projectName: string, sprint: Sprint): Observable<Sprint> {
    const startDateIso = (sprint.startDate) ? formatISO(new Date(sprint.startDate), { representation: 'date' }) : null;
    const endDateIso = (sprint.endDate) ? formatISO(new Date(sprint.endDate), { representation: 'date' }) : null;

    const data = {
      'name': sprint.name,
      'startDate': startDateIso,
      'endDate': endDateIso,
      'state': sprint.state
    };
    this.messageService.sendDebug(`SprintService createSprint called on sprint ${projectName}.`);
    // eslint-disable-next-line max-len
    return this.http.post<Sprint>(this.projectBaseURL + `/${projectName}/sprints/`, data).pipe(map(res => this.mapJsonToSprint(res)));
  }

  /**
 * Returns true if all issues (including subissues) of this sprint have status 'done'
 */
  checkAllIssuesDone(projectName: string, sprintId: number): Observable<boolean> {
    const targetURL = this.projectBaseURL + `/${projectName}/sprints/${sprintId}/?check-all-issues-done/`;
    return this.http.get<boolean>(targetURL);
  }

  /**
   * New sprints that are not yet persisted to JIRA get a temporary id.
   * We need an id to identify the sprint across multiple clients for sync-actions.
   * The temporary id is a negative number to easily distinguish it from a real/persistent id.
   * To get a unique id across multiple clients we use the current time millisec as id. This is of course
   * not -guaranteed- unique but the probability should be very high.
   */
  getTemporaryIdForNewSprint(): number {
    const tempId: number = new Date().getTime() * (-1);
    return tempId;
  }

  /**
 * this function is used to map json data to a sprint array
 */
  private mapJsonToSprintArray(jsonSprintDataArray: any): Sprint[] {
    return jsonSprintDataArray.map(element => this.mapJsonToSprint(element));
  }

  /**
   * Creates a real Sprint-Object from JSON sprint-data returned by pyWall
   */
  private mapJsonToSprint(data: any): Sprint {
    return new Sprint(Number(data.id), data.name, data.startDate, data.endDate, data.state);
  }

}

